import {differenceInMinutes, parseISO} from 'date-fns'
import {useMemo} from 'react'

import {ChatMessageFileAttachments, ChatMessages} from '../types/graphqlSchema'

const useGroupIncomingMediaMessages = (
  messages: ChatMessages[]
): GroupedChatMessage[] => useMemo(() => {
  if (!messages.length) return []
  const {currentGroup, result} = messages.reduce<AccumulatorState>(
    (acc, currentMessage) => {
      const isCustomerUserMessage = !!currentMessage.authorUser.customerUser
      const hasMedia = currentMessage.chatMessageFileAttachments.some(attachment =>
        MEDIA_MIME_TYPE_REGEX.test(attachment.mimeType)
      )
      const hasText = !!currentMessage.text?.trim()
      if (!isCustomerUserMessage || !hasMedia || hasText) {
        const newResult = [...acc.result]
        if (acc.currentGroup.length) {
          newResult.push(createGroupedMessage(acc.currentGroup))
        }
        newResult.push({...currentMessage})
        return {currentGroup: [], result: newResult}
      }
      const canGroupWithPrevious = (
        !!acc.currentGroup.length &&
        isWithinTimeWindow(
          acc.currentGroup[acc.currentGroup.length - 1], currentMessage
        ) &&
        acc.currentGroup.every(
          msg => msg.authorUser.id === currentMessage.authorUser.id
        )
      )
      if (canGroupWithPrevious) {
        return {...acc, currentGroup: [...acc.currentGroup, currentMessage]}
      }
      const newResult = [...acc.result]
      if (acc.currentGroup.length) {
        newResult.push(createGroupedMessage(acc.currentGroup))
      }
      return {currentGroup: [currentMessage], result: newResult}
    },
    {currentGroup: [], result: []}
  )
  const finalResult = [...result]
  if (currentGroup.length > 0) {
    finalResult.push(createGroupedMessage(currentGroup))
  }
  return finalResult
}, [messages])

const createGroupedMessage = (group: ChatMessages[]): GroupedChatMessage => {
  if (group.length <= 1) return {...group[0]}
  const allFileAttachments = group.flatMap(msg => msg.chatMessageFileAttachments)
  const lastMessage = group[group.length - 1]
  return {
    ...lastMessage,
    chatMessageFileAttachments: allFileAttachments,
    groupedMessages: group,
  }
}

const isWithinTimeWindow = (message1: ChatMessages, message2: ChatMessages): boolean => {
  if (!message1.timestamp || !message2.timestamp) return false
  const timestamp1 = parseISO(message1.timestamp as string)
  const timestamp2 = parseISO(message2.timestamp as string)
  return Math.abs(differenceInMinutes(timestamp1, timestamp2)) <=
    GROUPING_TIME_WINDOW_MINUTES
}

const GROUPING_TIME_WINDOW_MINUTES = 2
const MEDIA_MIME_TYPE_REGEX = (/(image|video)\//)

interface AccumulatorState {
  currentGroup: ChatMessages[]
  result: GroupedChatMessage[]
}

interface GroupedChatMessage extends Omit<ChatMessages, 'chatMessageFileAttachments'> {
  chatMessageFileAttachments: ChatMessageFileAttachments[]
  groupedMessages?: ChatMessages[]
}

export default useGroupIncomingMediaMessages
export type {GroupedChatMessage}
